import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Monogram } from "@/components/Logo";
import { getPaymentStatus } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center spotlight px-4 pt-28 pb-16">
      <div className="bg-cream border border-gold/40 frame-gold max-w-lg w-full p-10 sm:p-14 text-center">
        <div className="flex justify-center mb-8"><Monogram size={52} /></div>
        {state === "checking" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mb-4">Confirming</p>
            <h1 className="font-serif text-3xl font-light text-chocolate">Confirming your payment…</h1>
            <p className="text-sm text-chocolate/60 mt-4 leading-relaxed">One moment while we check with our secure payment provider.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mb-4">Order confirmed</p>
            <h1 className="font-serif text-3xl font-light text-chocolate">Thank you — <span className="italic text-golddark">it's on its way.</span></h1>
            <p className="text-sm text-chocolate/60 mt-4 leading-relaxed">
              Your payment is confirmed and your gift is being prepared in our signature wrapping. We'll be in touch with tracking shortly.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-chocolate/40 mt-6">Ref · {sessionId?.slice(-12)}</p>
            <Link to="/collection" data-testid="payment-success-continue"
              className="group inline-flex items-center gap-2 mt-8 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors">
              Continue Browsing <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mb-4">Almost there</p>
            <h1 className="font-serif text-3xl font-light text-chocolate">We're confirming your payment.</h1>
            <p className="text-sm text-chocolate/60 mt-4 leading-relaxed">
              Your bank is taking a little longer than usual. As soon as it clears we'll confirm by email — there's nothing you need to do.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mb-4">Hmm</p>
            <h1 className="font-serif text-3xl font-light text-chocolate">We couldn't confirm that payment.</h1>
            <p className="text-sm text-chocolate/60 mt-4 leading-relaxed">
              If you completed checkout, don't worry — contact us with your receipt and we'll confirm your order immediately.
            </p>
            <Link to="/faqs#contact" data-testid="payment-error-contact"
              className="group inline-flex items-center gap-2 mt-8 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors">
              Contact Us <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
